// Invitation redemption (RCS-43).
//
// The token in the URL is the credential. Loading this page only LOOKS at the
// invitation -- a reload must not burn it -- and accepting is a separate,
// deliberate action.
//
// Accepting now creates the station's Auth0 login and returns a one-time link
// where they set their own password. A server with no Auth0 credentials still
// accepts the invitation and says the sign-in was not created, which is what
// every local stack looks like -- so this page handles both, rather than
// promising a password screen that will not appear.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useTitle from './useTitle';
import './onboarding.css';

export default function InvitePage() {
  useTitle('Your invitation — Creator Rabbit');
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | accepting | done | bad
  const [invitation, setInvitation] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/onboarding/invitations/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setProblem(body.error_description || 'This invitation link is not valid.');
          setState('bad');
          return;
        }
        setInvitation(body);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setProblem('We could not reach the server. Please try again.');
        setState('bad');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    setState('accepting');
    try {
      const res = await fetch(`/api/onboarding/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setProblem(body.error_description || 'This invitation could not be accepted.');
        // A login that could not be created leaves the invitation usable, so
        // this is worth retrying rather than a dead end.
        setState(res.status === 502 ? 'ready' : 'bad');
        return;
      }
      setAccepted(body);
      setState('done');
      window.scrollTo(0, 0);
    } catch (err) {
      setProblem('We could not reach the server. Please try again.');
      setState('bad');
    }
  };

  if (state === 'loading') {
    return (
      <Shell>
        <p>Checking your invitation…</p>
      </Shell>
    );
  }

  if (state === 'bad') {
    return (
      <Shell>
        <h1>This invitation cannot be used</h1>
        <div className="ob-note bad" role="alert">
          <span>{problem}</span>
        </div>
        <p>
          Invitations are single use and expire. If you need a new one, reply to the email
          Creator Rabbit sent you and we will reissue it.
        </p>
      </Shell>
    );
  }

  if (state === 'done') {
    const setupUrl = accepted.account?.passwordSetupUrl;
    return (
      <Shell>
        <div className="ob-tick" aria-hidden="true">&#10003;</div>
        <h1>{accepted.stationName || accepted.tenantId} is reserved</h1>
        <p>
          Your invitation has been accepted and this link is now used up. Your station
          address is yours.
        </p>
        <dl className="ob-kv">
          <div>
            <dt>Station address</dt>
            <dd><code>{accepted.tenantId}.radiocustoms.com</code></dd>
          </div>
          <div>
            <dt>Account email</dt>
            <dd>{accepted.email}</dd>
          </div>
        </dl>

        {setupUrl ? (
          <>
            <p>
              One step left: choose a password for your sign-in. That link works once and
              is only for you. It opens in a new tab, so this page stays here.
            </p>
            {/* Deliberately a new tab. Our sign-in provider ends its password flow on a
                confirmation page of its own with no way onward -- verified 17 Sep 2026
                against the live tenant -- so navigating away would strand the station
                there. Opening beside this page means they close that tab and are back. */}
            <a className="ob-btn" href={setupUrl} target="_blank" rel="noopener noreferrer">
              Set your password
            </a>
            <div className="ob-note info">
              <span>
                Once you have set it, close that tab and you are done here. Creator Rabbit
                then configures your stream, branding and player with you, and your station
                goes live once that is done.
              </span>
            </div>
          </>
        ) : (
          <div className="ob-note warn">
            <span>
              <strong>Your sign-in was not created.</strong> That is a setting on this
              server rather than anything you did. Creator Rabbit finishes the setup with
              you &mdash; your stream, branding and player are configured from the admin
              console and your station goes live the same way.
            </span>
          </div>
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <h1>{invitation.stationName || 'Your station'} is ready for you</h1>
      <p>
        Creator Rabbit has reviewed your application and reserved your address. Accepting
        this invitation claims it and creates your sign-in.
      </p>
      <dl className="ob-kv">
        <div>
          <dt>Station address</dt>
          <dd><code>{invitation.tenantId}.radiocustoms.com</code></dd>
        </div>
        <div>
          <dt>Invited</dt>
          <dd>{invitation.email}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{new Date(invitation.expiresAt).toLocaleDateString(undefined, {
            day: 'numeric', month: 'long', year: 'numeric',
          })}</dd>
        </div>
      </dl>
      {problem && (
        <div className="ob-note bad" role="alert">
          <span>{problem}</span>
        </div>
      )}
      <div className="ob-note info">
        <span>
          This link works once. If you close this page before accepting, the link still
          works &mdash; it is only used up when you accept.
        </span>
      </div>
      <button className="ob-btn" onClick={accept} disabled={state === 'accepting'}>
        {state === 'accepting' ? 'Accepting…' : 'Accept invitation'}
      </button>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="ob-page">
      <div className="ob-bar">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="14" r="4" />
          <path d="M4 14a8 8 0 0 1 8-8" />
          <path d="M20 8.5 8.5 13" />
        </svg>
        <strong>Creator Rabbit</strong>
      </div>
      <div className="ob-wrap">
        <div className="ob-center">{children}</div>
      </div>
    </div>
  );
}
